import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Breadcrumbs,
  Menu,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "select2/dist/css/select2.min.css";
import "select2";
import Swal from "sweetalert2";
import "./RaiseComplaint.css";
import "./ProductTypeMasterManagement.css";
import CustomMultiSelect from "../components/common/CustomMultiSelect";
import { approvalWorkflowAPI, masterDataAPI, categoryMasterAPI } from "../services/api";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getDataTableExportRows } from "../utils/dataTableExportUtils";

const parseUserSpecificationFromWorkflowSource = (raw) => {
  if (typeof raw !== "string" || !raw.trim().startsWith("{")) return "None";
  try {
    const o = JSON.parse(raw);
    const s = o.userSpecificationType ?? o.UserSpecificationType;
    if (s == null || String(s).trim() === "") return "None";
    const t = String(s).trim();
    const norm = t === "LogHead" ? "CPD" : t;
    return ["None", "Plant", "RDC", "BCM", "CPD", "General"].includes(norm) ? norm : "None";
  } catch {
    return "None";
  }
};

const resolveMarketKind = (marketCode, marketName) => {
  const c = String(marketCode || "").trim().toUpperCase();
  const n = String(marketName || "").trim().toUpperCase();
  if (c === "M001" || n === "DOMESTIC" || c.includes("DOMESTIC") || (c.includes("DOM") && !c.includes("EXP"))) {
    return "Domestic";
  }
  if (c === "M002" || n === "EXPORT" || c.includes("EXPORT") || c.includes("EXP")) {
    return "Export";
  }
  return "";
};

const SR_JOURNEY_FLOW_DOMESTIC = [
  { id: "Plant", text: "Plant" },
  { id: "RDC", text: "RDC" },
  { id: "BCM", text: "BCM" },
  { id: "General", text: "General" },
];

const SR_JOURNEY_FLOW_EXPORT = [
  { id: "Plant", text: "Plant" },
  { id: "CPD", text: "CPD" },
  { id: "General", text: "General" },
];

const getSrJourneyFlowOptionsForMarkets = (marketCodes, marketsList) => {
  const codes = Array.isArray(marketCodes)
    ? marketCodes.map((c) => String(c).trim()).filter(Boolean)
    : [];
  if (!codes.length) return [];

  const kinds = new Set();
  for (const mc of codes) {
    const m = (marketsList || []).find(
      (x) =>
        String(x.marketCode || x.MARKETCODE || x.MarketCode || "").trim().toUpperCase() ===
        mc.toUpperCase()
    );
    const kind = resolveMarketKind(
      mc,
      m?.marketName || m?.MARKETNAME || m?.MarketName || ""
    );
    if (kind) kinds.add(kind);
  }

  if (kinds.has("Domestic") && kinds.has("Export")) {
    const byId = new Map();
    [...SR_JOURNEY_FLOW_DOMESTIC, ...SR_JOURNEY_FLOW_EXPORT].forEach((o) => byId.set(o.id, o));
    return [...byId.values()];
  }
  if (kinds.has("Export")) return [...SR_JOURNEY_FLOW_EXPORT];
  if (kinds.has("Domestic")) return [...SR_JOURNEY_FLOW_DOMESTIC];
  return [];
};

const workflowMatchesCommaSeparatedCodes = (storedValue, selectedCodes) => {
  if (!selectedCodes?.length) return true;
  const wanted = new Set(
    selectedCodes.map((c) => String(c).trim().toUpperCase()).filter(Boolean)
  );
  const raw = String(storedValue ?? "").trim();
  if (!raw) return false;
  return raw
    .split(",")
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean)
    .some((p) => wanted.has(p));
};

const normalizeWorkflow = (item, srTypesArray = []) => {
  const srTypeCode = item.srTypeCode || item.SrTypeCode || item.SRTYPECODE || "";
  // Get srTypeName from API response first (from stored procedure)
  let srTypeName = item.srTypeName || item.SrTypeName || item.SRTYPENAME || "";
  
  // If srTypeName is not in the API response, look it up from srTypesArray as fallback
  if (!srTypeName && srTypeCode && srTypesArray.length > 0) {
    const srType = srTypesArray.find(
      (st) => (st.SRTYPECODE || st.srTypeCode || "").toUpperCase() === srTypeCode.toUpperCase()
    );
    srTypeName = srType ? (srType.NAME || srType.name || "") : "";
  }
  
  return {
    workflowCode: item.workflowCode || item.WorkflowCode || item.WORKFLOWCODE || "",
    workflowName: item.workflowName || item.WorkflowName || item.WORKFLOWNAME || "",
    srTypeCode: srTypeCode,
    srTypeName: srTypeName,
    marketCode: item.marketCode || item.MarketCode || item.MARKETCODE || "",
    marketName: item.marketName || item.MarketName || item.MARKETNAME || "",
    catgCode: item.catgCode || item.CatgCode || item.CATGCODE || "",
    catgName: item.catgName || item.CatgName || item.CATGNAME || "",
    pageType: item.pageType || item.PageType || item.PAGETYPE || "SLATatConfiguration",
    workflowSource: item.workflowSource || item.WorkflowSource || item.WORKFLOWSOURCE || "",
    userSpecificationType: parseUserSpecificationFromWorkflowSource(
      item.workflowSource || item.WorkflowSource || item.WORKFLOWSOURCE || ""
    ),
    slaDays: item.slaDays ?? item.SlaDays ?? item.SLADAYS ?? null,
    tatDays: item.tatDays ?? item.TatDays ?? item.TATDAYS ?? null,
    isActive: item.isActive ?? item.IsActive ?? false,
    levelCount: item.levelCount ?? item.LevelCount ?? 0,
    createdBy: item.createdBy || item.CreatedBy || item.CREATEDBY || "",
    createdByName: item.createdByName || item.CreatedByName || item.CREATEDBYNAME || "",
    createdOn: item.createdOn || item.CreatedOn || null,
    modifiedBy: item.modifiedBy || item.ModifiedBy || item.MODIFIEDBY || "",
    modifiedByName: item.modifiedByName || item.ModifiedByName || item.MODIFIEDBYNAME || "",
    modifiedOn: item.modifiedOn || item.ModifiedOn || null,
  };
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

/** DataTable column indexes: Market, Business Vertical, Workflow, SR Type */
const SLA_TAT_TABLE_LEFT_HEADER_COLS = [1, 2, 3, 4];

const applySlaTatTableLeftHeaders = (api) => {
  if (!api) return;
  SLA_TAT_TABLE_LEFT_HEADER_COLS.forEach((colIdx) => {
    const header = api.column(colIdx).header();
    if (!header) return;
    const $th = $(header);
    $th.addClass("text-start").css("text-align", "left");
    $th.find(".dt-column-header, .dt-column-title").css({
      textAlign: "left",
      justifyContent: "flex-start",
    });
  });
  const $wrapper = $(api.table().node()).closest(".dataTables_wrapper, .dt-container");
  $wrapper.find(".dt-scroll-head thead th").each(function () {
    const colIdx = parseInt($(this).attr("data-dt-column"), 10);
    if (!SLA_TAT_TABLE_LEFT_HEADER_COLS.includes(colIdx)) return;
    const $th = $(this);
    $th.addClass("text-start").css("text-align", "left");
    $th.find(".dt-column-header, .dt-column-title").css({
      textAlign: "left",
      justifyContent: "flex-start",
    });
  });
};

const SLATatConfigurationManagement = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    srTypeCodes: [],
    marketCodes: [],
    catgCodes: [],
    userSpecificationType: "",
    pageType: "SLATatConfiguration",
  });
  const [workflows, setWorkflows] = useState([]);
  const [businessVerticals, setBusinessVerticals] = useState([]);
  const [srTypes, setSrTypes] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber] = useState(1);
  const [pageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [configMenuAnchor, setConfigMenuAnchor] = useState(null);
  const isConfigMenuOpenRef = useRef(false);
  const tableRef = useRef(null);
  const dataTableInstanceRef = useRef(null);
  const statusSelectRef = useRef(null);
  const pageTypeSelectRef = useRef(null);
  const isMountedRef = useRef(true);
  const filtersRef = useRef(filters);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const srJourneyFlowFilterOptions = useMemo(() => {
    const marketOpts = getSrJourneyFlowOptionsForMarkets(filters.marketCodes, markets);
    return [{ id: "", text: "All" }, ...marketOpts];
  }, [filters.marketCodes, markets]);

  useEffect(() => {
    const allowed = new Set(srJourneyFlowFilterOptions.map((o) => o.id));
    setFilters((prev) => {
      const current = String(prev.userSpecificationType || "").trim();
      if (!current || allowed.has(current)) return prev;
      return { ...prev, userSpecificationType: "" };
    });
  }, [srJourneyFlowFilterOptions]);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    isMountedRef.current = true;
    loadMarkets();
    fetchWorkflows();
    
    // Use document-level event delegation for status toggle (more reliable)
    const handleDocumentClick = (e) => {
      const $target = $(e.target);
      const $statusToggle = $target.closest(".status-toggle");
      if ($statusToggle.length && $(tableRef.current).find($statusToggle).length) {
        e.preventDefault();
        e.stopPropagation();
        // Use attr() instead of data() to avoid jQuery caching issues
        const code = $statusToggle.attr("data-code") || $statusToggle.data("code");
        const currentActiveStr = $statusToggle.attr("data-active") || $statusToggle.data("active");
        const currentActive =
          currentActiveStr === true ||
          currentActiveStr === "true" ||
          currentActiveStr === 1 ||
          currentActiveStr === "1";
        const newActive = !currentActive;
        console.log("Status toggle clicked (document):", { code, currentActive, newActive });
        if (code) {
          handleToggleStatus(code, newActive);
        } else {
          console.error("No workflow code found in status toggle button");
        }
      }
    };
    
    $(document).on("click", ".status-toggle", handleDocumentClick);
    
    return () => {
      isMountedRef.current = false;
      $(document).off("click", ".status-toggle", handleDocumentClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMarkets = async () => {
    try {
      const response = await masterDataAPI.getMarkets();
      if (response?.success && response.data) {
        const marketList = response.data.markets || response.data.Markets || [];
        setMarkets(marketList);
      }
    } catch (e) {
      console.error("Error loading markets:", e);
    }
  };

  const loadSRTypes = async (marketCode) => {
    if (!marketCode) {
      setSrTypes([]);
      return;
    }
    
    try {
      console.log("Loading SR Types for market:", marketCode);
      setSrTypes([]);
      
      const response = await masterDataAPI.getSRTypes(marketCode, true);
      console.log("SR Types API response:", response);
      
      let srTypesData = [];
      if (response?.success && response.data) {
        console.log("SR Types loaded successfully (success path):", response.data.length, response.data);
        srTypesData = Array.isArray(response.data) ? [...response.data] : [];
      } else if (response?.data && Array.isArray(response.data)) {
        console.log("SR Types loaded (direct array path):", response.data.length, response.data);
        srTypesData = [...response.data];
      } else if (Array.isArray(response)) {
        console.log("SR Types loaded (response is array):", response.length, response);
        srTypesData = [...response];
      } else {
        console.warn("No SR Types found for market:", marketCode, "Response:", response);
        srTypesData = [];
      }
      
      console.log("Final SR Types data to set:", srTypesData);
      setSrTypes(srTypesData);
      console.log("SR Types state updated with", srTypesData.length, "items");
    } catch (e) {
      console.error("Error loading SR Types for market", marketCode, ":", e);
      setSrTypes([]);
    }
  };


  const fetchWorkflows = async () => {
    const activeFilters = filtersRef.current;
    try {
      setLoading(true);
      const params = {
        srTypeCode:
          activeFilters.srTypeCodes?.length === 1
            ? String(activeFilters.srTypeCodes[0]).trim()
            : undefined,
        marketCode:
          activeFilters.marketCodes && activeFilters.marketCodes.length === 1
            ? String(activeFilters.marketCodes[0]).trim()
            : undefined,
        catgCode:
          activeFilters.catgCodes?.length === 1
            ? String(activeFilters.catgCodes[0]).trim()
            : undefined,
        isActive:
          activeFilters.status === "all"
            ? undefined
            : activeFilters.status === "active",
      };

      const response = await approvalWorkflowAPI.getAllSlaTat(params);

      if (response?.success && response.data) {
        let workflowsData = Array.isArray(response.data) ? response.data : [];

        if (activeFilters.marketCodes && activeFilters.marketCodes.length > 0) {
          const setM = new Set(
            activeFilters.marketCodes.map((c) => String(c).trim()).filter(Boolean)
          );
          workflowsData = workflowsData.filter((w) => {
            const mc = String(w.marketCode || w.MARKETCODE || "").trim();
            if (!mc) return false;
            const parts = mc.split(",").map((s) => s.trim()).filter(Boolean);
            return parts.some((p) => setM.has(p));
          });
        }

        if (activeFilters.catgCodes?.length > 1) {
          workflowsData = workflowsData.filter((w) =>
            workflowMatchesCommaSeparatedCodes(
              w.catgCode || w.CATGCODE,
              activeFilters.catgCodes
            )
          );
        }

        if (activeFilters.srTypeCodes?.length > 1) {
          workflowsData = workflowsData.filter((w) =>
            workflowMatchesCommaSeparatedCodes(
              w.srTypeCode || w.SRTYPECODE,
              activeFilters.srTypeCodes
            )
          );
        }

        if (activeFilters.userSpecificationType) {
          workflowsData = workflowsData.filter((w) => {
            const raw = w.workflowSource || w.WorkflowSource || "";
            return (
              parseUserSpecificationFromWorkflowSource(raw) ===
              activeFilters.userSpecificationType
            );
          });
        }
        
        // Filter by search text if provided
        if (activeFilters.search && activeFilters.search.trim()) {
          const searchLower = activeFilters.search.toLowerCase().trim();
          workflowsData = workflowsData.filter((w) => {
            const workflowName = (w.workflowName || w.WorkflowName || "").toLowerCase();
            const workflowCode = (w.workflowCode || w.WorkflowCode || "").toLowerCase();
            const srTypeCode = (w.srTypeCode || w.SRTYPECODE || "").toLowerCase();
            const catgCode = (w.catgCode || w.CATGCODE || "").toLowerCase();
                const marketCode = (w.marketCode || w.MARKETCODE || "").toLowerCase();
                return workflowName.includes(searchLower) || 
                   workflowCode.includes(searchLower) ||
                   srTypeCode.includes(searchLower) ||
                   catgCode.includes(searchLower) ||
                   marketCode.includes(searchLower);
          });
        }
        
        // Normalize workflows (level count is already included from stored procedure)
        const normalized = workflowsData.map((item) => normalizeWorkflow(item, srTypes));
        
        setWorkflows(normalized);
        setTotalRecords(normalized.length);
        initializeDataTable(normalized);
      } else {
        setWorkflows([]);
        setTotalRecords(0);
        initializeDataTable([]);
      }
    } catch (error) {
      console.error("Error fetching workflows:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load approval workflows",
      });
      setWorkflows([]);
      setTotalRecords(0);
      initializeDataTable([]);
    } finally {
      setLoading(false);
    }
  };

  const initializeDataTable = (data) => {
    if (dataTableInstanceRef.current) {
      try {
        const $table = $(tableRef.current);
        $table.off("click", ".levels-btn");
        $table.off("click", ".delete-workflow");
        $table.off("click", ".status-toggle");
        if ($.fn.DataTable && $.fn.DataTable.isDataTable(tableRef.current)) {
          dataTableInstanceRef.current.destroy();
        }
      } catch (e) {
        console.warn("Error destroying DataTable:", e);
      }
      dataTableInstanceRef.current = null;
    }

    if (tableRef.current) {
      try {
        if (!$.fn.DataTable) {
          DataTable($);
        }

        dataTableInstanceRef.current = $(tableRef.current).DataTable({
          data: data,
          paging: true,
          searching: true,
          info: true,
          lengthChange: true,
          lengthMenu: [5, 10, 25, 50, 100],
          dom: "lfrtip",
          ordering: true,
          destroy: true,
          scrollX: true,
          autoWidth: false,
          columns: [
            {
              title: "Workflow Code",
              data: "workflowCode",
              render: (d) => d || "-",
              visible: false,
            },
            {
              title: "Market",
              data: null,
              className: "text-start",
              render: (d, type, row) => row.marketName || row.MarketName || row.marketCode || row.MARKETCODE || "-",
            },
            {
              title: "Business Vertical",
              data: null,
              className: "text-start",
              render: (d, type, row) => row.catgName || row.CatgName || row.catgCode || row.CATGCODE || "-",
            },
            {
              title: "Workflow",
              data: "workflowName",
              className: "text-start",
              render: (d) => d || "-",
            },
            {
              title: "SR Type",
              data: "srTypeName",
              className: "text-start",
              render: (d, type, row) => {
                const srTypeName = d || row.srTypeName || row.SrTypeName || row.SRTYPENAME || "";
                if (!srTypeName || srTypeName === "") return "-";
                return srTypeName;
              },
            },
            {
              title: "Workflow Source",
              data: "workflowSource",
              className: "text-center",
              visible: false,
              render: (d) => d || "-",
            },
            {
              title: "TAT (Days)",
              data: "tatDays",
              className: "text-center",
              render: (d) => (d != null && d !== "" ? d : "-"),
            },
            {
              title: "Levels",
              data: "levelCount",
              className: "text-center",
              render: (data) => data || 0,
            },
            {
              title: "Created On",
              data: "createdOn",
              className: "text-center",
              render: (data) => formatDateTime(data),
            },
            {
              title: "Modified On",
              data: "modifiedOn",
              className: "text-center",
              render: (data) => formatDateTime(data),
            },
            {
              title: "Status",
              data: null,
              orderable: false,
              render: (d, type, row) => {
                // Extract workflowCode from row - handle different possible property names
                const workflowCode = row?.workflowCode || row?.WorkflowCode || row?.WORKFLOWCODE || "";
                const isActive = row?.isActive === true || row?.isActive === 1 || row?.IsActive === true || row?.IsActive === 1;
                const toggleClass = isActive ? "active" : "inactive";
                const toggleIcon = isActive ? "bi-toggle-on" : "bi-toggle-off";
                return `<div class="d-flex justify-content-center">
                  <button class="btn btn-sm status-toggle ${toggleClass}" 
                    data-code="${workflowCode}" 
                    data-active="${isActive}"
                    type="button"
                    title="${
                      isActive ? "Click to deactivate" : "Click to activate"
                    }"
                    style="background: transparent !important; 
                           border: none !important; 
                           padding: 0 !important;
                           font-size: 24px !important;
                           color: ${isActive ? "#4caf50" : "#9e9e9e"} !important;
                           cursor: pointer !important;
                           pointer-events: auto !important;
                           z-index: 10 !important;
                           position: relative !important;
                           transition: all 0.3s ease;">
                    <i class="bi ${toggleIcon}" style="pointer-events: none;"></i>
                  </button>
                </div>`;
              },
            },
            {
              title: "Action",
              data: null,
              orderable: false,
              searchable: false,
              render: (d) => {
                return `<div class="d-flex justify-content-center gap-2">
                  <button class="btn btn-sm levels-btn" data-code="${d.workflowCode}" data-page-type="SLATatConfiguration" title="Manage Levels"
                    style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border: none; color: white; padding: 8px 10px; border-radius: 6px;">
                    <i class="bi bi-list-ul"></i>
                  </button>
                  <button class="btn btn-sm delete-workflow" data-code="${d.workflowCode}" title="Delete Workflow"
                    style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); border: none; color: white; padding: 8px 10px; border-radius: 6px;">
                    <i class="bi bi-trash-fill"></i>
                  </button>
                </div>`;
              },
            },
          ],
          order: [[8, "desc"]], // Order by Created On descending
          language: {
            emptyTable: "No SLA & TAT workflows found",
          },
          initComplete: function () {
            applySlaTatTableLeftHeaders(this.api());
            try {
              const wrapper = $(tableRef.current).closest(
                ".dataTables_wrapper, .dt-container"
              );
              if (wrapper.length === 0) return;

              const lengthEl = wrapper.find(
                ".dt-length, .dataTables_length"
              );
              const searchEl = wrapper.find(
                ".dt-search, .dataTables_filter"
              );

              if (lengthEl.length > 0 || searchEl.length > 0) {
                let topWrap = wrapper.find(".dtFltrTop");
                if (topWrap.length === 0) {
                  topWrap = $(
                    "<div class='dtFltrTop d-flex flex-wrap align-items-center justify-content-between w-100'></div>"
                  );
                  wrapper.prepend(topWrap);
                }
                lengthEl.each(function () {
                  $(this).appendTo(topWrap);
                });
                searchEl.each(function () {
                  $(this).appendTo(topWrap);
                });
              }

              const infoEl = wrapper.find(".dt-info, .dataTables_info");
              const pagingEl = wrapper.find(
                ".dt-paging, .dataTables_paginate"
              );
              if (infoEl.length > 0 || pagingEl.length > 0) {
                let bottomWrap = wrapper.find(".dtFltrBotm");
                if (bottomWrap.length === 0) {
                  bottomWrap = $(
                    "<div class='dtFltrBotm d-flex flex-wrap align-items-center justify-content-between w-100'></div>"
                  );
                  wrapper.append(bottomWrap);
                }
                infoEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
                pagingEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
              }
            } catch (err) {
              console.warn("DataTable layout wrap failed:", err);
            }
          },
          headerCallback: function () {
            applySlaTatTableLeftHeaders(this.api());
          },
          drawCallback: function () {
            applySlaTatTableLeftHeaders(this.api());
            // Re-attach event handlers after each draw
            const $table = $(tableRef.current);
            $table
              .off("click", ".status-toggle")
              .on("click", ".status-toggle", function (e) {
                e.preventDefault();
                e.stopPropagation();
                const $btn = $(this);
                // Use attr() instead of data() to avoid jQuery caching issues
                const code = $btn.attr("data-code") || $btn.data("code");
                const currentActiveStr = $btn.attr("data-active") || $btn.data("active");
                const currentActive =
                  currentActiveStr === true ||
                  currentActiveStr === "true" ||
                  currentActiveStr === 1 ||
                  currentActiveStr === "1";
                const newActive = !currentActive;
                console.log("Status toggle clicked (drawCallback):", { code, currentActive, newActive });
                if (code) {
                  handleToggleStatus(code, newActive);
                } else {
                  console.error("No workflow code found in status toggle button");
                }
              });
          },
        });

        // Attach event handlers
        const $table = $(tableRef.current);
        $table
          .off("click", ".levels-btn")
          .on("click", ".levels-btn", function () {
            const code = $(this).data("code");
            navigate(`/sla-tat-configuration-level-setup/${code}?pageType=SLATatConfiguration`);
          });

        $table
          .off("click", ".delete-workflow")
          .on("click", ".delete-workflow", function () {
            const code = $(this).data("code");
            handleDelete(code);
          });

      // Attach status toggle handler - must be after DataTable initialization
      const $statusTable = $(tableRef.current);
      $statusTable
        .off("click", ".status-toggle")
        .on("click", ".status-toggle", function (e) {
          e.preventDefault();
          e.stopPropagation();
          const $btn = $(this);
          // Use attr() instead of data() to avoid jQuery caching issues
          const code = $btn.attr("data-code") || $btn.data("code");
          const currentActiveStr = $btn.attr("data-active") || $btn.data("active");
          const currentActive =
            currentActiveStr === true ||
            currentActiveStr === "true" ||
            currentActiveStr === 1 ||
            currentActiveStr === "1";
          const newActive = !currentActive;
          console.log("Status toggle clicked:", { code, currentActive, newActive, button: $btn[0] });
          if (code) {
            handleToggleStatus(code, newActive);
          } else {
            console.error("No workflow code found for status toggle");
          }
        });
      } catch (error) {
        console.error("Error initializing DataTable:", error);
      }
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    fetchWorkflows();
  };

  const handleReset = () => {
    setFilters({
      search: "",
      status: "all",
      srTypeCodes: [],
      marketCodes: [],
      catgCodes: [],
      userSpecificationType: "",
      pageType: "SLATatConfiguration",
    });
    setBusinessVerticals([]);
    if (statusSelectRef.current) {
      const $statusSelect = $(statusSelectRef.current);
      if ($statusSelect.data("select2")) {
        $statusSelect.val("all").trigger("change");
      }
    }
    setTimeout(() => {
      fetchWorkflows();
    }, 100);
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      status: "all",
      srTypeCodes: [],
      marketCodes: [],
      catgCodes: [],
      userSpecificationType: "",
      pageType: "SLATatConfiguration",
    });
    setBusinessVerticals([]);
    if (statusSelectRef.current) {
      const $statusSelect = $(statusSelectRef.current);
      if ($statusSelect.data("select2")) {
        $statusSelect.val("all").trigger("change");
      }
    }
    setTimeout(() => {
      fetchWorkflows();
    }, 100);
  };

  // Export to PDF
  const exportToPDF = () => {
    // Export what the grid is actually showing: when a search is active
    // only the matching rows are written out, otherwise the full data set.
    const exportRows = getDataTableExportRows(dataTableInstanceRef, workflows);
    try {
      if (!exportRows || exportRows.length === 0) {
        Swal.fire("Info", "No data to export", "info");
        return;
      }

      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text("SLA & TAT Configuration Data", 14, 15);

      const tableData = exportRows.map((workflow, index) => {
        const srTypeName = workflow.srTypeName || workflow.SrTypeName || workflow.SRTYPENAME || "-";
        return [
          String(index + 1),
          String(workflow.workflowName || "-"),
          String(workflow.marketName || workflow.marketCode || "-"),
          String(workflow.catgName || workflow.catgCode || "-"),
          String(srTypeName),
          String(workflow.workflowSource || "-"),
          String(workflow.tatDays != null ? workflow.tatDays : "-"),
          String(workflow.levelCount || 0),
          String(formatDateTime(workflow.createdOn) || "-"),
          String(formatDateTime(workflow.modifiedOn) || "-"),
          workflow.isActive ? "Active" : "Inactive",
        ];
      });

      autoTable(doc, {
        head: [["SNo.", "Workflow Name", "Market", "Category", "SR Type", "Workflow Source", "TAT (Days)", "Levels", "Created On", "Modified On", "Status"]],
        body: tableData,
        startY: 25,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [102, 126, 234] },
        margin: { top: 25 },
      });

      doc.save(`SLA_TAT_Configuration_${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      Swal.fire({
        title: "Error",
        text: `Failed to export PDF: ${error.message || "Unknown error"}`,
        icon: "error",
      });
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    // Export what the grid is actually showing: when a search is active
    // only the matching rows are written out, otherwise the full data set.
    const exportRows = getDataTableExportRows(dataTableInstanceRef, workflows);
    try {
      const headers = ["SNo.", "Workflow Name", "Market", "Category", "SR Type", "Workflow Source", "TAT (Days)", "Levels", "Created On", "Modified On", "Status"];
      const csvData = exportRows.map((workflow, index) => {
        const srTypeName = workflow.srTypeName || workflow.SrTypeName || workflow.SRTYPENAME || "";
        return [
          index + 1,
          workflow.workflowName || "",
          workflow.marketName || workflow.marketCode || "",
          workflow.catgName || workflow.catgCode || "",
          srTypeName,
          workflow.workflowSource || "",
          workflow.tatDays != null ? workflow.tatDays : "",
          workflow.levelCount || 0,
          formatDateTime(workflow.createdOn),
          formatDateTime(workflow.modifiedOn),
          workflow.isActive ? "Active" : "Inactive",
        ];
      });

      const csvContent = [
        headers.join(","),
        ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `SLA_TAT_Configuration_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      Swal.fire("Error", "Failed to export CSV", "error");
    }
  };

  // Export to Excel
  const exportToExcel = async () => {
    // Export what the grid is actually showing: when a search is active
    // only the matching rows are written out, otherwise the full data set.
    const exportRows = getDataTableExportRows(dataTableInstanceRef, workflows);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("SLA & TAT Configuration");

      worksheet.mergeCells("A1:K1");
      const titleRow = worksheet.getRow(1);
      titleRow.getCell(1).value = "SLA & TAT Configuration Data";
      titleRow.getCell(1).font = { size: 16, bold: true };
      titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
      titleRow.height = 25;

      const headerRow = worksheet.getRow(3);
      headerRow.values = ["SNo.", "Workflow Name", "Market", "Category", "SR Type", "Workflow Source", "TAT (Days)", "Levels", "Created On", "Modified On", "Status"];
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF667EEA" },
      };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };
      headerRow.height = 20;

      exportRows.forEach((workflow, index) => {
        const srTypeName = workflow.srTypeName || workflow.SrTypeName || workflow.SRTYPENAME || "";
        const row = worksheet.addRow([
          index + 1,
          workflow.workflowName || "",
          workflow.marketName || workflow.marketCode || "",
          workflow.catgName || workflow.catgCode || "",
          srTypeName,
          workflow.workflowSource || "",
          workflow.tatDays != null ? workflow.tatDays : "",
          workflow.levelCount || 0,
          formatDateTime(workflow.createdOn),
          formatDateTime(workflow.modifiedOn),
          workflow.isActive ? "Active" : "Inactive",
        ]);
        row.alignment = { vertical: "middle" };
      });

      worksheet.columns.forEach((column) => {
        column.width = 20;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `SLA_TAT_Configuration_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      Swal.fire("Error", "Failed to export Excel", "error");
    }
  };

  // Handle export menu
  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportOption = (format) => {
    handleExportMenuClose();
    if (format === "excel") {
      exportToExcel();
    } else if (format === "csv") {
      exportToCSV();
    } else if (format === "pdf") {
      exportToPDF();
    }
  };

  // Handle configuration menu
  const handleConfigMenuOpen = (event) => {
    if (event) {
      event.stopPropagation();
    }
    isConfigMenuOpenRef.current = true;
    setConfigMenuAnchor(event.currentTarget);
  };

  const handleConfigMenuClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setConfigMenuAnchor(null);
    // Delay resetting the ref to ensure menu is fully closed
    setTimeout(() => {
      isConfigMenuOpenRef.current = false;
    }, 200);
  };

  const handleConfigOption = (event) => {
    if (event) {
      event.stopPropagation();
    }
    handleConfigMenuClose(event);
    navigate("/sla-tat-configuration-level-setup?pageType=SLATatConfiguration");
  };

  const handleToggleStatus = async (workflowCode, newStatus) => {
    if (!workflowCode || workflowCode.trim() === "") {
      console.error("handleToggleStatus: workflowCode is empty or invalid", workflowCode);
      Swal.fire("Error", "Invalid workflow code", "error");
      return;
    }

    const actionText = newStatus ? "activate" : "deactivate";
    const result = await Swal.fire({
      title: "Confirm Status Change",
      text: `Are you sure you want to ${actionText} this approval workflow?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: newStatus ? "#4caf50" : "#ff9800",
      cancelButtonColor: "#6c757d",
      confirmButtonText: newStatus ? "Yes, activate!" : "Yes, deactivate!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        // Try to find workflow with case-insensitive comparison and handle different property names
        const workflow = workflows.find((w) => {
          const wCode = w.workflowCode || w.WorkflowCode || w.WORKFLOWCODE || "";
          return wCode && wCode.toString().toUpperCase() === workflowCode.toString().toUpperCase();
        });

        if (!workflow) {
          console.error("Workflow not found in workflows array:", {
            workflowCode,
            workflowsCount: workflows.length,
            workflows: workflows.map(w => ({
              code: w.workflowCode || w.WorkflowCode || w.WORKFLOWCODE,
              name: w.workflowName || w.WorkflowName
            }))
          });
          Swal.fire("Error", `Workflow not found: ${workflowCode}`, "error");
          return;
        }

        const modifiedBy = currentUser?.userIdCode || currentUser?.userName || "SYSADMIN001";
        const updatedWorkflow = { ...workflow, isActive: newStatus };
        const response = await approvalWorkflowAPI.update(workflowCode, updatedWorkflow);
        
        if (response && response.success) {
          Swal.fire(
            "Success!",
            response.message || `Approval workflow has been ${actionText}d.`,
            "success"
          );
          fetchWorkflows();
        } else {
          Swal.fire(
            "Error",
            response?.message || `Failed to ${actionText} approval workflow`,
            "error"
          );
        }
      } catch (error) {
        console.error(`Error ${actionText}ing approval workflow:`, error);
        Swal.fire(
          "Error",
          error?.response?.data?.message ||
            `Failed to ${actionText} approval workflow. Please try again.`,
          "error"
        );
      }
    }
  };

  const handleDelete = async (workflowCode) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the approval workflow and all its levels. This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await approvalWorkflowAPI.delete(workflowCode);
        if (response?.success) {
          Swal.fire("Deleted!", "The workflow has been deleted.", "success");
          fetchWorkflows();
        } else {
          Swal.fire("Error", response?.message || "Failed to delete workflow", "error");
        }
      } catch (error) {
        console.error("Error deleting workflow:", error);
        Swal.fire("Error", "Failed to delete workflow", "error");
      }
    }
  };

  // Cleanup Select2 before React removes nodes
  useLayoutEffect(() => {
    return () => {
      try {
        if (statusSelectRef.current) {
          const $status = $(statusSelectRef.current);
          if ($status.length && $status.data("select2")) {
            try {
              $status.off("change select2:select select2:change");
              if ($status.parent().length) {
                $status.select2("destroy");
              }
            } catch (e) {
              // Select2 might already be destroyed or node removed
            }
          }
        }
        if (pageTypeSelectRef.current) {
          const $pageType = $(pageTypeSelectRef.current);
          if ($pageType.length && $pageType.data("select2")) {
            try {
              $pageType.off("select2:select select2:change");
              if ($pageType.parent().length) {
                $pageType.select2("destroy");
              }
            } catch (e) {
              // Select2 might already be destroyed or node removed
            }
          }
        }
      } catch (e) {
        // Ignore cleanup errors
      }
    };
  });

  // Initialize Select2 dropdowns
  useEffect(() => {
    if (statusSelectRef.current) {
      const $select = $(statusSelectRef.current);
      try {
        if ($select.data("select2")) {
          $select.off("select2:select select2:change");
          $select.select2("destroy");
        }
      } catch (e) {
        console.warn("Error destroying Select2:", e);
      }
      
      if (typeof $.fn.select2 === 'undefined') {
        console.error("Select2 is not available for status dropdown");
        return;
      }
      
      try {
        if (!$select.parent().length) {
          console.warn("Status select element not in DOM yet");
          return;
        }
        
        $select.select2({
          width: "100%",
          minimumResultsForSearch: Infinity,
        });
        $select.on("select2:select select2:change", function (e) {
          const selectedStatus = $select.val() || "";
          handleFilterChange("status", selectedStatus);
        });
      } catch (error) {
        console.error("Error initializing Status Select2:", error);
      }
    }
  }, []);

  // Initialize Select2 for Configuration (PageType) dropdown
  useEffect(() => {
    if (pageTypeSelectRef.current) {
      const $select = $(pageTypeSelectRef.current);
      try {
        if ($select.data("select2")) {
          $select.off("select2:select select2:change");
          $select.select2("destroy");
        }
      } catch (e) {
        console.warn("Error destroying Select2:", e);
      }
      
      if (typeof $.fn.select2 === 'undefined') {
        console.error("Select2 is not available for configuration dropdown");
        return;
      }
      
      try {
        if (!$select.parent().length) {
          console.warn("Configuration select element not in DOM yet");
          return;
        }
        
        $select.select2({
          width: "100%",
          placeholder: "Select Configuration",
          allowClear: true,
        });
        $select.on("select2:select select2:change", function (e) {
          const selectedPageType = $select.val() || "";
          handleFilterChange("pageType", selectedPageType);
        });
        
        // Set initial value if filters.pageType exists
        if (filters.pageType) {
          $select.val(filters.pageType).trigger("change.select2");
        }
      } catch (error) {
        console.error("Error initializing Configuration Select2:", error);
      }
    }
  }, [filters.pageType]);


  useEffect(() => {
    const codes = filters.marketCodes || [];
    if (!codes.length) {
      setBusinessVerticals([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const merged = [];
        const seen = new Set();
        for (const marketCode of codes) {
          const catRes = await categoryMasterAPI.getPaged({
            marketCode,
            isActive: true,
            pageNumber: 1,
            pageSize: 5000,
          });
          const cats =
            catRes?.data?.Categories ||
            catRes?.data?.categories ||
            catRes?.data?.data?.Categories ||
            [];
          if (Array.isArray(cats)) {
            for (const cat of cats) {
              const id = cat.catgCode || cat.CATGCODE || cat.CatgCode || "";
              const key = String(id).trim().toUpperCase();
              if (key && !seen.has(key)) {
                seen.add(key);
                merged.push(cat);
              }
            }
          }
        }
        if (!cancelled) setBusinessVerticals(merged);
      } catch (e) {
        console.error("Failed to load business verticals for filters", codes, e);
        if (!cancelled) setBusinessVerticals([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [(filters.marketCodes || []).join("|")]);

  useEffect(() => {
    const first = String(filters.marketCodes?.[0] || "").trim();
    if (first) loadSRTypes(first);
    else setSrTypes([]);
  }, [(filters.marketCodes || []).join("|")]);

  useEffect(() => {
    fetchWorkflows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    <Box sx={{ position: "relative", minHeight: loading ? 360 : undefined }}>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 30,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255, 255, 255, 0.82)",
            borderRadius: 1,
          }}
          aria-live="polite"
          aria-busy="true"
        >
          <CircularProgress size={40} aria-label="Loading SLA and TAT configurations" />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading configurations...
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          opacity: loading ? 0.55 : 1,
          pointerEvents: loading ? "none" : "auto",
        }}
      >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            className="add-edit-product-breadcrumb"
          >
            <Typography
              color="text.primary"
              className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
            >
              SLA & TAT Configuration
            </Typography>
          </Breadcrumbs>
          <Typography variant="body2" sx={{ color: "#666666", mt: 1 }}>
            Manage SLA & TAT configuration.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {/* Add Configuration Button */}
          <Button
            type="button"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleConfigOption(e);
            }}
            sx={{
              background: "linear-gradient(135deg, #dc3545 0%, #ff0022 100%)",
              border: "none",
              color: "white",
              fontWeight: 600,
              fontSize: "11px",
              boxShadow: "0 4px 15px rgba(245, 87, 108, 0.4)",
              borderRadius: "4px",
              textTransform: "uppercase",
              padding: "8px 24px",
              "&:hover": {
                background: "linear-gradient(135deg, #dc3545 0%, #ff0022 100%)",
                boxShadow: "0 6px 20px rgba(245, 87, 108, 0.6)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Add SLA & TAT
          </Button>
          {/* Export Button with Dropdown */}
          <Button
            onClick={handleExportMenuOpen}
            sx={{
              background: "transparent",
              border: "1px solid #667eea",
              color: "#667eea",
              fontWeight: 500,
              padding: "8px 10px",
              borderRadius: "6px",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "36px",
              height: "36px",
              "&:hover": {
                background: "transparent",
                borderColor: "#764ba2",
                color: "#764ba2",
              },
              "& .MuiSvgIcon-root": {
                color: "#667eea",
              },
              "&:hover .MuiSvgIcon-root": {
                color: "#764ba2",
              },
            }}
            title="Export"
          >
            <FileDownloadIcon sx={{ fontSize: "26px", filter: "drop-shadow(0 0 0.5px currentColor)" }} />
            <ArrowDropDownIcon sx={{ fontSize: "16px", ml: 0.5 }} />
          </Button>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={handleExportMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem onClick={() => handleExportOption("excel")}>
              Excel
            </MenuItem>
            <MenuItem onClick={() => handleExportOption("csv")}>
              CSV
            </MenuItem>
            <MenuItem onClick={() => handleExportOption("pdf")}>
              PDF
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Filter Panel */}
      <div className="card-panel mb-3 filter-panel">
        <div
          className="section-heading d-flex align-items-center gap-2 mb-3"
          style={{ marginBottom: "14px" }}
        >
          <SearchIcon
            fontSize="small"
            className="text-muted"
            style={{
              width: "18px",
              height: "18px",
              flexShrink: 0,
              marginTop: "2px",
            }}
          />
          <span>Filter: Market, Business Vertical, SR Type, SR Journey Flow </span>
        </div>

        <div className="container-fluid">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-6 col-lg-3 srchDvwrpr d-none" aria-hidden="true">
              <label className="input-label d-block mb-1 text-start">
                Search
              </label>
              <div className="input-icon" style={{ position: "relative" }}>
                <input
                  type="text"
                  className="form-control form-control-light text-start"
                  placeholder="Search by Code / Name"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleFilterChange("search", filters.search.trim());
                      fetchWorkflows();
                    }
                  }}
                  style={{ height: "42px", textAlign: "left" }}
                />
                <SearchIcon
                  className="icon-right"
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    width: "16px",
                    height: "16px",
                    color: "#6c757d",
                  }}
                />
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">Market</label>
                <CustomMultiSelect
                  options={(markets || [])
                    .map((m) => ({
                      id: m.marketCode || m.MARKETCODE || m.MarketCode || "",
                      text:
                        m.marketName ||
                        m.MARKETNAME ||
                        m.MarketName ||
                        m.marketCode ||
                        m.MARKETCODE ||
                        m.MarketCode ||
                        "",
                    }))
                    .filter((o) => o.id)}
                  value={
                    filters.marketCodes?.length
                      ? [String(filters.marketCodes[0]).trim()]
                      : []
                  }
                  onChange={(selected) => {
                    const picked =
                      Array.isArray(selected) && selected.length ? selected[0] : "";
                    const code = picked ? String(picked).trim() : "";
                    setFilters((prev) => ({
                      ...prev,
                      marketCodes: code ? [code] : [],
                      catgCodes: [],
                      srTypeCodes: [],
                      userSpecificationType: "",
                    }));
                  }}
                  placeholder="Select Market"
                  disabled={false}
                  showSelectAll={false}
                  singleSelect
                />
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">Business Vertical</label>
                <CustomMultiSelect
                  options={(businessVerticals || [])
                    .map((cat) => ({
                      id: cat.catgCode || cat.CATGCODE || cat.CatgCode || "",
                      text:
                        cat.catgName ||
                        cat.CATGNAME ||
                        cat.CatgName ||
                        cat.catgCode ||
                        cat.CATGCODE ||
                        "",
                    }))
                    .filter((o) => o.id && o.text)}
                  value={filters.catgCodes || []}
                  onChange={(selected) =>
                    handleFilterChange("catgCodes", Array.isArray(selected) ? selected : [])
                  }
                  placeholder={
                    !filters.marketCodes?.length
                      ? "Select Market first"
                      : "Select Business Vertical"
                  }
                  disabled={!filters.marketCodes?.length}
                  showSelectAll
                />
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">SR Type</label>
                <CustomMultiSelect
                  options={(srTypes || [])
                    .map((st) => ({
                      id: String(st.SRTYPECODE || st.srTypeCode || st.SRTypeCode || "").trim(),
                      text:
                        String(st.NAME || st.name || st.Name || st.SRTYPECODE || st.srTypeCode || "").trim() ||
                        String(st.SRTYPECODE || st.srTypeCode || "").trim(),
                    }))
                    .filter((o) => o.id)}
                  value={filters.srTypeCodes || []}
                  onChange={(selected) =>
                    handleFilterChange("srTypeCodes", Array.isArray(selected) ? selected : [])
                  }
                  placeholder={
                    !filters.marketCodes?.length ? "Select Market first" : "Select SR Type"
                  }
                  disabled={!filters.marketCodes?.length}
                  showSelectAll
                  selectAllLabel="Select all"
                />
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <div className="select2-wrapper">
                <label className="select2-label pnlLblsze">SR Journey Flow</label>
                <CustomMultiSelect
                  options={srJourneyFlowFilterOptions}
                  value={[
                    filters.userSpecificationType === "" || filters.userSpecificationType == null
                      ? ""
                      : filters.userSpecificationType,
                  ]}
                  onChange={(selected) => {
                    const picked =
                      Array.isArray(selected) && selected.length ? selected[0] : "";
                    handleFilterChange("userSpecificationType", picked === undefined ? "" : picked);
                  }}
                  placeholder={
                    !filters.marketCodes?.length ? "Select Market first" : "All"
                  }
                  disabled={!filters.marketCodes?.length}
                  showSelectAll={false}
                  singleSelect
                />
              </div>
            </div>

{/*             <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">
                Status
              </label>
              <select
                ref={statusSelectRef}
                className="form-select form-control-light filter-select text-start"
                value={filters.status}
                style={{ height: "42px", textAlign: "left" }}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div> */}

            <div className="col-12 col-md-6 col-lg-3">
              <label
                className="input-label d-block mb-1 text-start"
                style={{ visibility: "hidden" }}
              >
                Actions
              </label>
              <div className="d-flex gap-2 filter-buttons justify-content-start">
                <button
                  type="button"
                  className="btn btn-filter"
                  disabled={loading}
                  onClick={() => {
                    handleFilterChange("search", filters.search.trim());
                    fetchWorkflows();
                  }}
                  style={{
                    background:
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 24px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #0f00f5 0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(102, 126, 234, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(102, 126, 234, 0.4)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                >
                  {loading ? (
                    <CircularProgress size={18} sx={{ color: "white", mr: 1 }} />
                  ) : (
                    <SearchIcon fontSize="small" className="me-1" />
                  )}
                  {loading ? "Loading..." : "Search"}
                </button>
                <button
                  type="button"
                  className="btn btn-reset-filter"
                  onClick={resetFilters}
                  title="Reset"
                  style={{
                    background:
                      "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(132, 250, 176, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 16px",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(132, 250, 176, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(132, 250, 176, 0.4)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                >
                  <RestartAltIcon fontSize="small" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Paper
        sx={{
          p: 0,
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      >
        <div className="card-panel mt-0">
          {!loading && workflows.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No workflows found. Click "Add Configuration" to create a new SLA & TAT configuration.
              </Typography>
            </Box>
          ) : (
            <div className="table-responsive">
              <table
                ref={tableRef}
                className="table table-striped table-bordered complaint-table custmTblLst"
                style={{ width: "100%" }}
              />
            </div>
          )}
        </div>
      </Paper>

      </Box>
    </Box>
  );
};

export default SLATatConfigurationManagement;
